import React from 'react';

interface LoadingScreenProps {
  title?: string;
  message?: string;
  showDots?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'full' | 'inline' | 'minimal';
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({
  title = "Loading",
  message = "Please wait...",
  showDots = true,
  size = 'lg',
  variant = 'full'
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12', 
    lg: 'w-16 h-16'
  };

  const titleSizes = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl'
  };

  if (variant === 'minimal') {
    return (
      <div className="flex items-center justify-center gap-3">
        <div className={`${sizeClasses[size]} relative`}>
          <div className="absolute inset-0 rounded-full border-2 border-dark-600"></div>
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-accent-purple animate-spin"></div>
        </div>
        <span className="text-dark-300">{message}</span>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className="text-center py-8">
        <div className={`${sizeClasses[size]} mx-auto relative mb-4`}>
          <div className="absolute inset-0 rounded-full border-4 border-dark-600"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-accent-purple animate-spin"></div>
        </div>
        <div className="text-dark-300">{message}</div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <div className="relative mb-8">
        {/* Animated spinner */}
        <div className={`${sizeClasses[size]} mx-auto relative`}>
          <div className="absolute inset-0 rounded-full border-4 border-dark-600"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-accent-purple animate-spin"></div>
          <div 
            className="absolute inset-2 rounded-full border-2 border-transparent border-t-accent-pink animate-spin" 
            style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}
          ></div>
        </div>
        
        {/* Pulsing background effect */}
        <div className="absolute inset-0 -z-10">
          <div className="w-32 h-32 mx-auto bg-gradient-to-r from-accent-purple/20 to-accent-pink/20 rounded-full blur-xl animate-pulse"></div>
        </div>
      </div>
      
      <h2 className={`${titleSizes[size]} font-bold text-white mb-3`}>{title}</h2>
      <p className="text-dark-300 mb-6">{message}</p>
      
      {/* Loading dots animation */}
      {showDots && (
        <div className="flex justify-center space-x-2">
          <div className="w-2 h-2 bg-accent-purple rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-accent-pink rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-accent-purple rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      )}
    </div>
  );
};

export default LoadingScreen;